import {
  buildValidationGroups,
  type ValidationGroup,
  type ValidationItem,
  type ValidationReport,
} from '@photovalidator/core';
import { el } from '../ui/dom.js';

/**
 * The result column — the entire report, and nothing else.
 *
 * Contract (v1.3, product rules):
 *  - ONE verdict card, the group accordions, ONE call to action. No advice, no
 *    "what to do", no source links, no disclaimer paragraphs, no thresholds
 *    section: only what was checked, the measured value, the official range and
 *    the status;
 *  - the groups come from the core (`buildValidationGroups`) in the order it
 *    returns them, closed on first render; a new report rebuilds the panel, so
 *    nothing is remembered between reports;
 *  - `not_checked` items are not rows: only a muted counter in the group header;
 *  - the measured value is data, the official range is data. A missing
 *    capability is explained by one short notice whose technical reasons stay in
 *    `?debug=1`.
 */

export type ResultStatus = ValidationItem['status'];

/** The only four statuses (and the only four icons) the interface ever shows. */
export const RESULT_STATUS_ICON: Record<ResultStatus, string> = {
  pass: '✅',
  warning: '⚠',
  fail: '❌',
  uncertain: '?',
};

const VERDICT_HEADLINE: Record<ValidationReport['overall']['verdict'], string> = {
  fail: '❌ ФОТО НЕ СООТВЕТСТВУЕТ',
  pass: '✅ ФОТО СООТВЕТСТВУЕТ',
  needs_review: '⚠ ФОТО ТРЕБУЕТ ПРОВЕРКИ',
};

const plural = (count: number, forms: readonly [string, string, string]): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
};

const notCheckedLabel = (count: number): string =>
  `${count} ${plural(count, ['не проверялось', 'не проверялись', 'не проверялись'])}`;

/**
 * One friendly banner for every missing capability (face model not loaded,
 * quality stage skipped…). It explains why some checks could not run; the raw
 * reasons are deliberately NOT here — they live in `?debug=1`.
 */
export const createCapabilityNotice = (issues: readonly string[]): HTMLElement =>
  el(
    'div',
    {
      class: 'pv-result__notice pv-unsupported',
      role: 'status',
      'data-issues': issues.length,
    },
    [
      el('p', {
        class: 'pv-unsupported__title',
        text: '⚠ Не удалось выполнить часть автоматической проверки.',
      }),
      el('p', {
        class: 'pv-small',
        text: 'Проверки, которым не хватило данных, отмечены в заголовках групп и не влияют на вердикт.',
      }),
    ],
  );

/**
 * One compact item row: status icon, label, measured value (right-aligned) and
 * the official range as a small second line. `details`, when present, is only a
 * `title` attribute — never visible prose, and never an instruction.
 */
const itemElement = (item: ValidationItem, onSelect?: (checkId: string) => void): HTMLElement => {
  const head = el('button', { class: 'pv-item__head', type: 'button' }, [
    el('span', {
      class: 'pv-item__icon',
      'aria-hidden': 'true',
      text: RESULT_STATUS_ICON[item.status],
    }),
    el('span', { class: 'pv-item__label', text: item.label }),
    ...(item.value !== undefined
      ? [el('span', { class: 'pv-item__value', text: item.value })]
      : []),
  ]);
  /**
   * Clicking a row highlights that measurement on the photo (diagnostics only:
   * nothing is moved, cropped or edited). It is the only interaction an item has.
   */
  if (onSelect) head.addEventListener('click', () => onSelect(item.id));

  const children: Array<Node | string> = [
    el('div', { class: 'pv-item__row' }, [head]),
  ];
  if (item.allowedText) {
    children.push(el('div', { class: 'pv-item__range', text: item.allowedText }));
  }
  return el(
    'li',
    {
      // `pv-status-*` is the public DOM contract (the E2E suite relies on it);
      // `pv-item--*` is the styling hook.
      class: `pv-item pv-item--${item.status} pv-status-${item.status}`,
      'data-check-id': item.id,
      ...(item.details ? { title: item.details } : {}),
    },
    children,
  );
};

/** One accordion per group; a real `<details>` so the toggle works without JS. */
const groupElement = (
  group: ValidationGroup,
  onSelect?: (checkId: string) => void,
): HTMLElement => {
  const chevron = el('span', {
    class: 'pv-group__chevron',
    'aria-hidden': 'true',
    text: '▶',
  });
  const head = el('summary', { class: 'pv-group__head', 'aria-expanded': 'false' }, [
    chevron,
    el('span', { class: 'pv-group__title', text: group.title }),
    ...(group.notChecked > 0
      ? [
          el('span', {
            class: 'pv-group__not-checked',
            text: `· ${notCheckedLabel(group.notChecked)}`,
          }),
        ]
      : []),
    el('span', {
      class: 'pv-group__icon',
      'aria-hidden': 'true',
      text: RESULT_STATUS_ICON[group.status],
    }),
  ]);

  const details = el('details', {
    class: `pv-group pv-group--${group.status}`,
    'data-group': group.id,
  });
  // Every item is in the DOM even while the group is closed.
  details.append(
    head,
    el(
      'ul',
      { class: 'pv-group__items' },
      group.items.map((item) => itemElement(item, onSelect)),
    ),
  );
  details.addEventListener('toggle', () => {
    const open = details.open;
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    chevron.textContent = open ? '▼' : '▶';
  });
  return details;
};

const verdictCard = (report: ValidationReport, groupsPassed: number): HTMLElement => {
  const verdict = report.overall.verdict;
  const card = el(
    'div',
    {
      class: `pv-verdict-card pv-verdict-card--${verdict}`,
      'data-verdict': verdict,
      'data-role': 'verdict',
    },
    [el('p', { class: 'pv-verdict-card__headline', text: VERDICT_HEADLINE[verdict] })],
  );

  if (verdict === 'pass') {
    card.append(el('p', { class: 'pv-verdict-card__done', text: 'Проверка завершена' }));
    return card;
  }

  const { problems, warnings } = report.overall.userSummary;
  card.append(
    el('ul', { class: 'pv-verdict-card__counters' }, [
      el('li', {
        'data-count': 'problems',
        text: `${problems} ${plural(problems, ['проблема', 'проблемы', 'проблем'])}`,
      }),
      el('li', {
        'data-count': 'warnings',
        text: `${warnings} ${plural(warnings, [
          'предупреждение',
          'предупреждения',
          'предупреждений',
        ])}`,
      }),
      el('li', {
        'data-count': 'groups-passed',
        text: `${groupsPassed} ${plural(groupsPassed, [
          'группа пройдена',
          'группы пройдены',
          'групп пройдено',
        ])}`,
      }),
    ]),
  );
  return card;
};

/** The single call to action: a plain link to the host product, nothing else. */
const ctaLink = (
  report: ValidationReport,
  onCtaClick?: () => void,
): HTMLElement | null => {
  const url = report.cta?.url;
  if (!url) return null;
  const passed = report.overall.verdict === 'pass';
  const link = el('a', {
    class: 'pv-result__cta pv-button pv-button--primary',
    'data-cta': 'primary',
    href: url,
    target: '_blank',
    rel: 'noopener',
    text: passed ? 'Продолжить' : 'Сделать фото в ГОСТ ФОТО',
  });
  // The link itself stays a plain HTTPS link: the handler only counts the click.
  if (onCtaClick) link.addEventListener('click', () => onCtaClick());
  return link;
};

export interface ResultPanelOptions {
  /** Missing-capability reasons; only their number is used, never their text. */
  capabilityIssues?: readonly string[];
  /** Highlights one measurement on the photo; diagnostics only. */
  onSelect?(checkId: string): void;
  /** Counts a click on the single call to action (analytics only). */
  onCtaClick?(): void;
}

export const createResultPanel = (
  report: ValidationReport | undefined,
  options: ResultPanelOptions = {},
): HTMLElement => {
  const issues = options.capabilityIssues ?? [];
  const container = el('section', { class: 'pv-panel pv-result', 'data-role': 'result' });

  if (!report) {
    if (issues.length > 0) container.append(createCapabilityNotice(issues));
    container.append(
      el('p', {
        class: 'pv-result__empty pv-small pv-muted',
        text: 'Результат проверки появится здесь после загрузки фотографии.',
      }),
    );
    return container;
  }

  const groups = buildValidationGroups(report);
  const groupsPassed = groups.filter((group) => group.status === 'pass').length;

  container.append(verdictCard(report, groupsPassed));
  if (issues.length > 0) container.append(createCapabilityNotice(issues));
  container.append(
    el(
      'div',
      { class: 'pv-result__groups' },
      groups.map((group) => groupElement(group, options.onSelect)),
    ),
  );

  const cta = ctaLink(report, options.onCtaClick);
  if (cta) container.append(cta);

  return container;
};
